import { useEffect, useState, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { getToken } from "./tokenStorage";
import { useTheme } from "./useTheme";
import WeekRecap from "./WeekRecap";
import OnlineIndicator from "./OnlineIndicator";

const GRADIENT_MAP = {
  blue:   "linear-gradient(135deg,#3b82f6 0%,#2563eb 60%,#7c3aed 100%)",
  ocean:  "linear-gradient(135deg,#06b6d4 0%,#0ea5e9 60%,#6366f1 100%)",
  green:  "linear-gradient(135deg,#10b981 0%,#059669 60%,#0891b2 100%)",
  rose:   "linear-gradient(135deg,#f43f5e 0%,#ec4899 60%,#a855f7 100%)",
  orange: "linear-gradient(135deg,#f97316 0%,#ef4444 60%,#dc2626 100%)",
  dark:   "linear-gradient(135deg,#52555e 0%,#2a2c32 60%,#1a1b1f 100%)",
};
const GRADIENT_FALLBACKS = Object.values(GRADIENT_MAP);

const ROLE_LABEL = {
  admin:             "Администратор",
  teacher:           "Преподаватель",
  student:           "Студент",
  "мастер сикс севен": "Мастер Сикс Севен",
};

function resolveAvatar(url) {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith("data:")) return url;
  return `${window.location.origin}${url}`;
}

function getGradient(u) {
  return GRADIENT_MAP[u.profile_gradient] || GRADIENT_FALLBACKS[(u.id || 0) % GRADIENT_FALLBACKS.length];
}

function CrownIcon({ size = 20 }) {
  return (
    <img
      src="/badges/golden_crown.svg"
      alt="Корона лидера"
      width={size}
      height={size}
      style={{ display: "inline-block", verticalAlign: "middle", objectFit: "contain" }}
    />
  );
}

function PaperclipBadge({ count }) {
  if (!count && count !== 0) return null;
  return (
    <span className="flex items-center gap-0.5 text-[12px] text-slate-400 tabular-nums shrink-0 ml-1">
      <img src="/paperclip_silver.svg" alt="" style={{ width: 12, height: 12, objectFit: 'contain' }} />
      {count}
    </span>
  );
}

function UserRow({ u, isLast }) {
  const avatarSrc = resolveAvatar(u.avatar_url);
  const frameSrc = resolveAvatar(u.avatar_frame);
  const gradient = getGradient(u);

  const subtitle = [
    ROLE_LABEL[u.role] || u.role,
    u.is_curator ? "Куратор" : null,
    u.study_group || null,
  ].filter(Boolean).join(" · ");

  return (
    <Link
      to={`/profile/${u.id}`}
      className="group relative flex items-center gap-3.5 bg-white px-4 py-3 transition-colors duration-100 hover:bg-slate-50/70 active:bg-slate-100/80"
      style={{ WebkitTapHighlightColor: "transparent" }}
    >
      <div className="relative shrink-0 w-[42px] h-[42px]" data-clip-zone="users_avatar" data-uid={u.id}>
        <div
          className="w-[42px] h-[42px] rounded-full overflow-hidden flex items-center justify-center"
          style={{ background: gradient }}
        >
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[16px] font-semibold text-white select-none leading-none">
              {(u.fio || "?")[0].toUpperCase()}
            </span>
          )}
        </div>
        {frameSrc && (
          <img src={frameSrc} alt="" className="absolute inset-0 w-full h-full pointer-events-none select-none" style={{ transform: `translateX(${u.avatar_frame_offset_x ?? 0}%) translateY(${u.avatar_frame_offset_y ?? 0}%) scale(${(u.avatar_frame_scale ?? 100) / 100})`, transformOrigin: "center" }} />
        )}
        <span className="absolute bottom-0 right-0" style={{ zIndex: 1 }}>
          <OnlineIndicator online={!!u.is_online} device={u.device} size={12} deviceSize={14} />
        </span>
      </div>

      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-medium text-slate-900 leading-snug flex items-center gap-1 min-w-0">
          <span className="truncate">{u.fio || "—"}</span>
          {u.is_developer && u.dev_badge_equipped !== false && (
            <img src="/badges/developer_status.svg" alt="Разработчик" style={{ width: 18, height: 18, flexShrink: 0 }} />
          )}
          {u.equipped_badge_asset && (
            <img src={u.equipped_badge_asset} alt="" style={{ width: 18, height: 18, flexShrink: 0, objectFit: "contain" }} />
          )}
        </p>
        <p className="text-[12px] text-slate-400 leading-snug mt-px truncate">{subtitle}</p>
      </div>

      <PaperclipBadge count={u.skrep_balance} />

      <svg
        viewBox="0 0 8 14"
        className="w-2 h-3.5 shrink-0 text-slate-300 transition-transform duration-150 group-hover:translate-x-0.5"
        fill="none"
      >
        <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {!isLast && (
        <div className="pointer-events-none absolute bottom-0 left-[70px] right-0 h-px bg-slate-100" />
      )}
    </Link>
  );
}

function CrownInfoModal({ onClose }) {
  const { isDark } = useTheme();
  return createPortal(
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      <style>{`@keyframes crownModalIn{from{opacity:0;transform:scale(0.05)}to{opacity:1;transform:scale(1)}}@keyframes crownDropIn{from{opacity:0}to{opacity:1}}`}</style>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" style={{ animation: 'crownDropIn 200ms ease both' }} />
      <div
        className="relative rounded-2xl shadow-2xl p-6 mx-4 max-w-xs w-full flex flex-col items-center gap-3"
        style={{ animation: 'crownModalIn 300ms cubic-bezier(0.34,1.56,0.64,1) both', transformOrigin: 'center', background: isDark ? '#1c1c1e' : '#ffffff' }}
        onClick={e => e.stopPropagation()}
      >
        <button type="button" onClick={onClose} className="absolute top-3 right-3 w-7 h-7 rounded-full flex items-center justify-center hover:opacity-70 transition-opacity" style={{ color: isDark ? '#6b7280' : '#94a3b8' }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M1 1l12 12M13 1L1 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/></svg>
        </button>
        <img src="/badges/golden_crown.svg" alt="Корона лидера" style={{ width: 96, height: 96, objectFit: 'contain' }} />
        <p className="text-[15px] font-semibold text-center" style={{ color: isDark ? '#ffffff' : '#1e293b' }}>Корона недели</p>
        <p className="text-[13px] text-center leading-relaxed" style={{ color: isDark ? '#9ca3af' : '#64748b' }}>
          Переходит к игроку, который сейчас <b style={{ color: isDark ? '#e5e7eb' : '#475569' }}>#1</b> по скрепкам за неделю. На сбор не влияет — чистый статус.<br/><br/>
          <b style={{ color: isDark ? '#e5e7eb' : '#475569' }}>Награда:</b> в конце недели каждый, кто носил корону, получает золотые скрепки по числу 2-часовых окон владения.<br/><br/>
          Чем дольше держал корону — тем больше золота. Неделя закрывается в понедельник 00:00.
        </p>
      </div>
    </div>,
    document.body
  );
}

function WeeklyRow({ entry, rank, isLast, isFirst: isFirstProp, rankDelta, isNew, animIndex = 0 }) {
  const avatarSrc = resolveAvatar(entry.avatar_url);
  const frameSrc = resolveAvatar(entry.avatar_frame);
  const gradient = getGradient(entry);
  const isFirst = rank === 1 || isFirstProp;
  const [crownInfo, setCrownInfo] = useState(false);

  const badgeDelay = animIndex * 55 + 280;
  const showUp   = rankDelta !== null && rankDelta > 0;
  const showDown = rankDelta !== null && rankDelta < 0;

  return (
    <>
    <Link
      to={`/profile/${entry.id}`}
      className="group relative flex items-center gap-3.5 bg-white dark:bg-transparent px-4 transition-colors duration-100 hover:bg-slate-50/70 dark:hover:bg-slate-700/30 active:bg-slate-100/80 dark:active:bg-slate-700/40"
      style={{ WebkitTapHighlightColor: "transparent", paddingTop: 12, paddingBottom: 12, animation: `weeklyRowIn 340ms cubic-bezier(0.2,0.9,0.2,1) ${animIndex * 55}ms both` }}
    >
      {/* Rank + delta badge */}
      <div className="relative w-6 shrink-0 flex items-center justify-center">
        <span className={`text-[13px] font-bold tabular-nums ${isFirst ? "text-amber-500" : rank <= 3 ? "text-slate-500 dark:text-slate-400" : "text-slate-300 dark:text-slate-600"}`}>
          {rank}
        </span>
        {(showUp || showDown || isNew) && (
          <span
            className={`absolute -top-1 -right-2.5 text-[9px] font-bold px-[3px] py-px rounded-full leading-none whitespace-nowrap ${
              showUp   ? "text-emerald-600 bg-emerald-50 dark:bg-emerald-900/30 dark:text-emerald-400" :
              isNew    ? "text-blue-600 bg-blue-50 dark:bg-blue-900/30 dark:text-blue-400" :
                         "text-red-500 bg-red-50 dark:bg-red-900/30 dark:text-red-400"
            }`}
            style={{ animation: `rankBadgeIn 420ms cubic-bezier(0.34,1.56,0.64,1) ${badgeDelay}ms both` }}
          >
            {showUp ? `↑${rankDelta}` : isNew ? "new" : `↓${Math.abs(rankDelta)}`}
          </span>
        )}
      </div>

      {/* Avatar */}
      <div className="relative shrink-0">
        <div
          className="w-[42px] h-[42px] rounded-full overflow-hidden flex items-center justify-center"
          style={{ background: gradient }}
        >
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[16px] font-semibold text-white select-none leading-none">
              {(entry.fio || "?")[0].toUpperCase()}
            </span>
          )}
        </div>
        {entry.is_crown_holder && (
          <img
            src="/badges/golden_crown.svg"
            alt="Корона лидера"
            onClick={e => { e.preventDefault(); e.stopPropagation(); setCrownInfo(true); }}
            style={{ position: 'absolute', top: -22, left: 4, width: '80%', height: '80%', objectFit: 'cover', cursor: 'pointer', zIndex: 10, animation: `crownDropIn 500ms cubic-bezier(0.34,1.56,0.64,1) ${animIndex * 55 + 180}ms both` }}
          />
        )}
        {frameSrc && !entry.is_crown_holder && (
          <img src={frameSrc} alt="" className="absolute inset-0 w-full h-full pointer-events-none select-none" style={{ transform: `translateX(${entry.avatar_frame_offset_x ?? 0}%) translateY(${entry.avatar_frame_offset_y ?? 0}%) scale(${(entry.avatar_frame_scale ?? 100) / 100})`, transformOrigin: "center" }} />
        )}
      </div>

      {/* Name */}
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-medium text-slate-900 dark:text-slate-100 leading-snug flex items-center gap-1 min-w-0">
          <span className="truncate">{entry.fio || "—"}</span>
          {entry.equipped_badge_asset && (
            <img src={entry.equipped_badge_asset} alt="" style={{ width: 18, height: 18, flexShrink: 0, objectFit: "contain" }} />
          )}
        </p>
        <p className="text-[12px] text-slate-400 dark:text-slate-500 leading-snug mt-px truncate">
          {ROLE_LABEL[entry.role] || entry.role}{entry.study_group ? ` · ${entry.study_group}` : ""}
        </p>
      </div>

      {/* Weekly clips */}
      <span className={`flex items-center gap-1 text-[13px] font-semibold shrink-0 tabular-nums ${isFirst ? "text-amber-500" : "text-slate-500 dark:text-slate-400"}`}>
        <img src="/paperclip_silver.svg" alt="" style={{ width: 16, height: 16, objectFit: 'contain', opacity: isFirst ? 1 : 0.7 }} />
        {entry.weekly_clips}
      </span>

      <svg viewBox="0 0 8 14" className="w-2 h-3.5 shrink-0 text-slate-300 dark:text-slate-600 transition-transform duration-150 group-hover:translate-x-0.5" fill="none">
        <path d="M1 1l6 6-6 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>

      {!isLast && (
        <div className="pointer-events-none absolute bottom-0 left-[70px] right-0 h-px bg-slate-100 dark:bg-slate-700/50" />
      )}
    </Link>
    {crownInfo && <CrownInfoModal onClose={() => setCrownInfo(false)} />}
  </>
  );
}

function timeUntilNextMondayParts() {
  const now = new Date();
  const day = now.getDay();
  const daysLeft = day === 0 ? 1 : 8 - day;
  const target = new Date(now);
  target.setDate(now.getDate() + daysLeft);
  target.setHours(0, 0, 0, 0);
  const diffMs = Math.max(0, target - now);
  const totalSec = Math.floor(diffMs / 1000);
  return {
    days: Math.floor(totalSec / 86400),
    hours: Math.floor((totalSec % 86400) / 3600),
    minutes: Math.floor((totalSec % 3600) / 60),
    seconds: totalSec % 60,
    totalSec,
  };
}

function ResetClock({ color = 'currentColor', size = 14 }) {
  const { isDark } = useTheme();
  const [, setTick] = useState(0);
  const [showTip, setShowTip] = useState(false);

  useEffect(() => {
    const id = setInterval(() => setTick((n) => (n + 1) & 0xffff), 30_000);
    return () => clearInterval(id);
  }, []);

  const now = new Date();
  const minuteAngle = (now.getMinutes() + now.getSeconds() / 60) * 6;
  const hourAngle = ((now.getHours() % 12) + now.getMinutes() / 60) * 30;

  const t = timeUntilNextMondayParts();
  const label = t.days > 0
    ? `До сброса: ${t.days} д ${t.hours} ч ${t.minutes} мин`
    : t.hours > 0
      ? `До сброса: ${t.hours} ч ${t.minutes} мин`
      : `До сброса: ${t.minutes} мин ${t.seconds} с`;

  return (
    <span
      className="relative inline-flex items-center select-none"
      onMouseEnter={() => setShowTip(true)}
      onMouseLeave={() => setShowTip(false)}
      aria-label={label}
      title={label}
    >
      <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
        <circle cx="12" cy="12" r="9.5" stroke={color} strokeWidth="1.4" fill="none" />
        <line
          x1="12" y1="12" x2="12" y2="7.5"
          stroke={color} strokeWidth="1.7" strokeLinecap="round"
          style={{ transform: `rotate(${hourAngle}deg)`, transformOrigin: '12px 12px' }}
        />
        <line
          x1="12" y1="12" x2="12" y2="5.5"
          stroke={color} strokeWidth="1.3" strokeLinecap="round"
          style={{ transform: `rotate(${minuteAngle}deg)`, transformOrigin: '12px 12px' }}
        />
      </svg>
      {showTip && (
        <div
          className="absolute right-0 top-full mt-1.5 px-2 py-1 rounded-md text-[11px] whitespace-nowrap z-50 pointer-events-none font-normal"
          style={{
            background: isDark ? '#2a2c32' : '#ffffff',
            color: isDark ? '#e5e7eb' : '#334155',
            border: `1px solid ${isDark ? '#3a3c42' : '#e2e8f0'}`,
            boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
            maxWidth: 'calc(100vw - 16px)',
          }}
        >
          {label}
        </div>
      )}
    </span>
  );
}

export default function UsersList() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sortByClips, setSortByClips] = useState(false);
  const [tab, setTab] = useState("all");
  const [weeklyData, setWeeklyData] = useState(null);
  const [weeklyLoading, setWeeklyLoading] = useState(false);
  const [recapData, setRecapData] = useState(null);
  const [recapPhase, setRecapPhase] = useState("none"); // 'none' | 'intro' | 'spotlight' | 'done'
  const [recapHighlight, setRecapHighlight] = useState(null); // rank currently spotlit
  const [recapRevealed, setRecapRevealed] = useState(() => new Set());
  const recapTimeoutsRef = useRef([]);
  const searchRef = useRef(null);
  const prevRanksRef = useRef({});   // { [userId]: rank }
  const hasPrevRanksRef = useRef(false);


  useEffect(() => {
    fetch("/auth/users?limit=500", { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => r.json())
      .then((data) => { setUsers(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (tab !== "weekly") return;
    let cancelled = false;

    const fetchWeekly = async (isFirst) => {
      if (isFirst) setWeeklyLoading(true);
      try {
        const r = await fetch("/skrep/leaderboard/weekly", { headers: { Authorization: `Bearer ${getToken()}` } });
        const data = await r.json();
        if (cancelled) return;
        setWeeklyData(prev => {
          if (prev?.entries) {
            const ranks = {};
            prev.entries.forEach((e, i) => { ranks[e.id] = i + 1; });
            prevRanksRef.current = ranks;
            hasPrevRanksRef.current = true;
          }
          return data;
        });
      } catch {}
      finally { if (!cancelled) setWeeklyLoading(false); }
    };

    fetchWeekly(true);
    const interval = setInterval(() => fetchWeekly(false), 10_000);
    return () => { cancelled = true; clearInterval(interval); };
  }, [tab]);

  // Fetch week-recap once on entering weekly tab.
  useEffect(() => {
    if (tab !== "weekly") return;
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch("/skrep/week-recap", { headers: { Authorization: `Bearer ${getToken()}` } });
        if (!r.ok) return;
        const data = await r.json();
        if (cancelled) return;
        if (!data?.has_recap) return;
        setRecapData(data);
        const seenKey = `week_recap_shown_${data.week_start}`;
        if (!localStorage.getItem(seenKey)) {
          localStorage.setItem(seenKey, "1"); // пометить сразу — чтобы не повторялось при каждом заходе
          setRecapPhase("intro");
        }
      } catch {}
    })();
    return () => { cancelled = true; };
  }, [tab]);

  const clearRecapTimers = () => {
    recapTimeoutsRef.current.forEach(clearTimeout);
    recapTimeoutsRef.current = [];
  };

  // Intro done → start spotlight sweep (bottom → top).
  const startSpotlight = () => {
    if (!recapData?.participants?.length) {
      setRecapPhase("done");
      return;
    }
    const participants = [...recapData.participants].sort((a, b) => b.rank - a.rank);
    const PER_ROW = 1000;      // окна показались + сконвертировались в золотые
    const CHAMP_HOLD = 2400;   // чемпион — дольше
    setRecapPhase("spotlight");
    setRecapHighlight(null);
    setRecapRevealed(new Set());

    let acc = 150; // small gap after intro fades out
    participants.forEach((p, i) => {
      const isChamp = i === participants.length - 1;
      const dwell = isChamp ? CHAMP_HOLD : PER_ROW - 50;
      const highlightAt = acc;
      const revealAt = acc + dwell;
      acc += PER_ROW;
      recapTimeoutsRef.current.push(setTimeout(() => setRecapHighlight(p.rank), highlightAt));
      recapTimeoutsRef.current.push(setTimeout(() => {
        setRecapRevealed(prev => {
          const next = new Set(prev);
          next.add(p.rank);
          return next;
        });
      }, revealAt));
    });
    recapTimeoutsRef.current.push(setTimeout(() => {
      setRecapHighlight(null);
      setRecapPhase("done");
    }, acc + CHAMP_HOLD - PER_ROW + 200));
  };

  const dismissRecap = () => {
    if (recapData?.week_start) {
      localStorage.setItem(`week_recap_shown_${recapData.week_start}`, "1");
    }
    clearRecapTimers();
    setRecapPhase("none");
    setRecapHighlight(null);
    setRecapRevealed(new Set());
  };

  const replayRecap = () => {
    clearRecapTimers();
    setRecapHighlight(null);
    setRecapRevealed(new Set());
    setRecapPhase("intro");
  };

  useEffect(() => () => clearRecapTimers(), []);

  // Dev-trigger: window.__showRecap('champion'|'holder'|'none')
  useEffect(() => {
    const champMock = {
      user_id: 999, fio: "Старцев Владимир", avatar_url: null,
      windows_held: 52, golden_awarded: 12, final_count: 78,
    };
    const mockParticipants = [
      { rank: 1,  user_id: 999, fio: "Старцев Владимир", avatar_url: null, windows_held: 52, golden_awarded: 12, final_count: 78 },
      { rank: 2,  user_id: 101, fio: "Иванов Пётр",      avatar_url: null, windows_held: 31, golden_awarded: 9,  final_count: 64 },
      { rank: 3,  user_id: 102, fio: "Петров Иван",      avatar_url: null, windows_held: 18, golden_awarded: 7,  final_count: 53 },
      { rank: 4,  user_id: 103, fio: "Сидорова Анна",    avatar_url: null, windows_held: 12, golden_awarded: 6,  final_count: 44 },
      { rank: 5,  user_id: 104, fio: "Козлов Максим",    avatar_url: null, windows_held: 7,  golden_awarded: 4,  final_count: 37 },
      { rank: 6,  user_id: 105, fio: "Новикова Мария",   avatar_url: null, windows_held: 4,  golden_awarded: 3,  final_count: 28 },
      { rank: 7,  user_id: 106, fio: "Фёдоров Денис",    avatar_url: null, windows_held: 2,  golden_awarded: 2,  final_count: 21 },
      { rank: 8,  user_id: 107, fio: "Волкова Елена",    avatar_url: null, windows_held: 1,  golden_awarded: 2,  final_count: 17 },
      { rank: 9,  user_id: 108, fio: "Морозов Артём",    avatar_url: null, windows_held: 0,  golden_awarded: 0,  final_count: 11 },
      { rank: 10, user_id: 109, fio: "Григорьева Ольга", avatar_url: null, windows_held: 0,  golden_awarded: 0,  final_count: 6  },
    ];
    const buildMock = (variant) => {
      const base = {
        week_start: "2026-10-05", week_end: "2026-10-12",
        has_recap: true, champion: champMock, my_result: null,
        participants: mockParticipants.map(p => ({ ...p })),
      };
      if (variant === "champion") {
        base.my_result = {
          rank: 1, windows_held: 52, golden_awarded: 12, final_count: 78,
          is_champion: true, held_crown: true,
        };
      } else if (variant === "holder") {
        base.my_result = {
          rank: 4, windows_held: 12, golden_awarded: 6, final_count: 44,
          is_champion: false, held_crown: true,
        };
      } else if (variant === "low") {
        // Юзер в хвосте, корону не держал — себя подставляем на 9 место.
        base.my_result = {
          rank: 9, windows_held: 0, golden_awarded: 0, final_count: 11,
          is_champion: false, held_crown: false,
        };
      } else if (variant === "none") {
        base.my_result = null;
      }
      return base;
    };
    window.__showRecap = (variant = "champion") => {
      clearRecapTimers();
      setRecapHighlight(null);
      setRecapRevealed(new Set());
      setRecapData(buildMock(variant));
      setTab("weekly");
      setRecapPhase("intro");
    };
    window.__resetRecapSeen = () => {
      Object.keys(localStorage).filter(k => k.startsWith("week_recap_shown_")).forEach(k => localStorage.removeItem(k));
      console.log("Recap seen flags cleared");
    };
    return () => { delete window.__showRecap; delete window.__resetRecapSeen; };
  }, []);

  useEffect(() => {
    if (!loading) window.dispatchEvent(new CustomEvent("pageContentReady"));
  }, [loading]);

  const sections = useMemo(() => {
    const q = search.toLowerCase().trim();
    const pool = q
      ? users.filter(
          (u) =>
            u.fio?.toLowerCase().includes(q) ||
            u.email?.toLowerCase().includes(q) ||
            u.study_group?.toLowerCase().includes(q)
        )
      : users;

    if (sortByClips) {
      const sorted = [...pool].sort((a, b) => (b.skrep_balance || 0) - (a.skrep_balance || 0));
      return sorted.length ? [{ letter: null, items: sorted }] : [];
    }

    const sorted = [...pool].sort((a, b) =>
      (a.fio || "").localeCompare(b.fio || "", "ru")
    );

    if (q) return sorted.length ? [{ letter: null, items: sorted }] : [];

    const map = new Map();
    for (const u of sorted) {
      const key = u.study_group || "__nogroup__";
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(u);
    }

    const noGroup = map.get("__nogroup__") || [];
    map.delete("__nogroup__");

    const sortGroup = (items) =>
      items.sort((a, b) => {
        if (a.is_curator && !b.is_curator) return -1;
        if (!a.is_curator && b.is_curator) return 1;
        return 0;
      });

    const result = Array.from(map.entries())
      .sort(([a], [b]) => a.localeCompare(b, "ru"))
      .map(([key, items]) => ({ letter: key, items: sortGroup(items) }));

    if (noGroup.length) result.push({ letter: "Без группы", items: sortGroup(noGroup) });
    return result;
  }, [users, search, sortByClips]);

  const onlineCount = useMemo(() => users.filter((u) => u.is_online).length, [users]);

  return (
    <div className="users-list-shell space-y-3">
      {/* Header */}
      <div className="flex items-baseline justify-between px-0.5 pb-1">
        <h1 className="text-[22px] font-semibold tracking-tight text-slate-900">Участники</h1>
        {!loading && (
          <span className="text-[13px] text-slate-400 tabular-nums">
            {users.length} · {onlineCount} в сети
          </span>
        )}
      </div>

      {/* Tab switcher */}
      <div className="flex gap-1 bg-slate-100 rounded-[10px] p-1">
        <button
          type="button"
          onClick={() => setTab("all")}
          className={`flex-1 py-1.5 rounded-[8px] text-[14px] font-medium transition-all duration-150 ${tab === "all" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}
        >
          Все
        </button>
        <button
          type="button"
          onClick={() => setTab("weekly")}
          className={`flex-1 py-1.5 rounded-[8px] text-[14px] font-medium transition-all duration-150 flex items-center justify-center gap-1.5 ${tab === "weekly" ? "bg-white text-amber-600 shadow-sm" : "text-slate-500"}`}
        >
          <CrownIcon size={14} />
          Топ недели
          <ResetClock size={13} />
        </button>
      </div>

      {tab === "all" && (
        <>
          {/* Search + sort */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <svg viewBox="0 0 20 20" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="9" r="5.5" />
                <path d="m13.5 13.5 3 3" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder="Поиск"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full rounded-[10px] bg-slate-200/60 pl-9 pr-8 py-2.5 text-[15px] text-slate-900 placeholder:text-slate-400 outline-none focus:bg-slate-200/90 transition-colors"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => { setSearch(""); searchRef.current?.focus(); }}
                  className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center justify-center w-[18px] h-[18px] rounded-full bg-slate-400 transition-colors hover:bg-slate-500"
                  aria-label="Очистить"
                >
                  <svg viewBox="0 0 10 10" className="w-2.5 h-2.5 text-white" fill="none">
                    <path d="M2 2l6 6M8 2l-6 6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
                  </svg>
                </button>
              )}
            </div>
            {/* Sort toggle */}
            <button
              type="button"
              onClick={() => setSortByClips((v) => !v)}
              title={sortByClips ? "Сортировка по скрепкам" : "Сортировка по алфавиту"}
              className={`shrink-0 flex items-center justify-center w-[42px] h-[42px] rounded-[10px] transition-colors ${sortByClips ? "bg-amber-100" : "bg-slate-200/60"}`}
            >
              <img src="/paperclip_silver.svg" alt="" style={{ width: 20, height: 20, objectFit: 'contain', opacity: sortByClips ? 1 : 0.5 }} />
            </button>
          </div>

          {/* Content */}
          {loading ? (
            <div className="space-y-4 pt-2">
              {[6, 4, 5].map((count, si) => (
                <div key={si}>
                  <div className="h-3 w-4 rounded bg-slate-200 mb-2 ml-0.5 animate-pulse" />
                  <div className="rounded-[14px] overflow-hidden border border-slate-200/50 bg-white">
                    {Array.from({ length: count }).map((_, i) => (
                      <div key={i} className="relative flex items-center gap-3.5 px-4 py-3">
                        <div className="w-[42px] h-[42px] rounded-full bg-slate-100 shrink-0 animate-pulse" />
                        <div className="flex-1 space-y-2">
                          <div className="h-3 w-32 rounded-full bg-slate-100 animate-pulse" />
                          <div className="h-2.5 w-20 rounded-full bg-slate-100 animate-pulse" />
                        </div>
                        {i < count - 1 && <div className="absolute bottom-0 left-[70px] right-0 h-px bg-slate-100" />}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : sections.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-center">
              <div className="w-12 h-12 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <svg className="w-6 h-6 text-slate-300" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <circle cx="11" cy="11" r="7" />
                  <path d="m21 21-4.35-4.35" strokeLinecap="round" />
                </svg>
              </div>
              <p className="text-[15px] text-slate-400">Никого не найдено</p>
              <button type="button" onClick={() => setSearch("")} className="mt-3 text-[14px] text-blue-500 font-medium">
                Сбросить поиск
              </button>
            </div>
          ) : (
            <>
              <div data-clip-zone="users_list" style={{ height: 0 }} aria-hidden="true" />
              <div className="space-y-5">
                {sections.map(({ letter, items }) => (
                  <div key={letter ?? "_search"}>
                    {letter && (
                      <p className="text-[11px] font-bold uppercase tracking-[0.06em] text-slate-400 mb-1.5 px-0.5">{letter}</p>
                    )}
                    <div className="overflow-hidden rounded-[14px] border border-slate-200/50 bg-white shadow-[0_1px_4px_rgba(15,23,42,0.06)]">
                      {items.map((u, i) => (
                        <UserRow key={u.id} u={u} isLast={i === items.length - 1} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </>
      )}

      {tab === "weekly" && (
        <div className="space-y-3">
          {recapData?.has_recap && (
            <button
              type="button"
              onClick={replayRecap}
              className="w-full flex items-center justify-between px-3 py-2 rounded-[10px] text-[12px] font-medium text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-500/10 border border-amber-200/60 dark:border-amber-500/20 hover:bg-amber-100 dark:hover:bg-amber-500/15 transition-colors"
            >
              <span className="flex items-center gap-1.5">
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
                </svg>
                Итоги прошлой недели
              </span>
              <span className="opacity-60">→</span>
            </button>
          )}
          <style>{`
            @keyframes weeklyRowIn { from { opacity:0; transform:translateY(10px) } to { opacity:1; transform:translateY(0) } }
            @keyframes rankBadgeIn { 0%{opacity:0;transform:scale(0.4)} 60%{transform:scale(1.25)} 100%{opacity:1;transform:scale(1)} }
            @keyframes crownDropIn { 0%{opacity:0;transform:translateY(-8px) scale(0.7)} 60%{transform:translateY(2px) scale(1.1)} 100%{opacity:1;transform:translateY(0) scale(1)} }
            @keyframes flashCham { 0%{opacity:0} 15%{opacity:0.4} 100%{opacity:0} }
            @keyframes recapCtaIn { from { opacity:0; transform:translateY(8px) } to { opacity:1; transform:translateY(0) } }
            @keyframes convertSpark { 0%{opacity:0;transform:translateY(-50%) scale(0.3)} 40%{opacity:1;transform:translateY(-50%) scale(1.4)} 100%{opacity:0;transform:translateY(-50%) scale(2.2)} }
          `}</style>

          {(recapPhase === "spotlight" || recapPhase === "done") && recapData?.participants?.length ? (
            <>
              <div className="rounded-[14px] border border-amber-300/40 dark:border-amber-500/20 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none" style={{ overflow: 'visible' }}>
                {recapData.participants.map((p, i) => (
                  <WeeklyRecapRow
                    key={p.user_id}
                    p={p}
                    isMe={recapData.my_result?.rank === p.rank}
                    isLast={i === recapData.participants.length - 1}
                    highlight={recapHighlight === p.rank}
                    revealed={recapRevealed.has(p.rank)}
                    animIndex={i}
                  />
                ))}
              </div>
              {recapPhase === "done" && (
                <button
                  type="button"
                  onClick={dismissRecap}
                  className="mx-auto block mt-2 px-6 py-2.5 rounded-full text-[14px] font-semibold text-slate-900 hover:scale-[1.03] active:scale-[0.98] transition-transform"
                  style={{
                    background: "linear-gradient(135deg,#fde68a 0%,#f59e0b 100%)",
                    boxShadow: "0 8px 24px -8px rgba(251,191,36,0.5)",
                    animation: "recapCtaIn 400ms ease both",
                  }}
                >
                  К текущей неделе →
                </button>
              )}
            </>
          ) : weeklyLoading && !weeklyData ? (
            <div className="rounded-[14px] overflow-hidden border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32]">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="relative flex items-center gap-3.5 px-4 py-3">
                  <div className="w-6 h-3 rounded bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                  <div className="w-[42px] h-[42px] rounded-full bg-slate-100 dark:bg-slate-700/50 shrink-0 animate-pulse" />
                  <div className="flex-1 space-y-2">
                    <div className="h-3 w-32 rounded-full bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                    <div className="h-2.5 w-20 rounded-full bg-slate-100 dark:bg-slate-700/50 animate-pulse" />
                  </div>
                  {i < 4 && <div className="absolute bottom-0 left-[82px] right-0 h-px bg-slate-100 dark:bg-slate-700/50" />}
                </div>
              ))}
            </div>
          ) : !weeklyData || weeklyData.entries?.length === 0 ? (
            <div className="flex flex-col items-center py-20 text-center">
              <div className="w-12 h-12 rounded-full bg-amber-50 dark:bg-amber-900/20 flex items-center justify-center mb-3">
                <CrownIcon size={24} />
              </div>
              <p className="text-[15px] text-slate-400 dark:text-slate-500">На этой неделе ещё никто не собирал скрепки</p>
            </div>
          ) : (
            <div className="rounded-[14px] border border-slate-200/50 dark:border-slate-700/40 bg-white dark:bg-[#2a2c32] shadow-[0_1px_4px_rgba(15,23,42,0.06)] dark:shadow-none" style={{ overflow: 'visible' }}>
              {weeklyData.entries.map((entry, i) => {
                const oldRank = hasPrevRanksRef.current ? (prevRanksRef.current[entry.id] ?? null) : undefined;
                const rankDelta = (oldRank != null) ? oldRank - (i + 1) : null;
                const isNew = hasPrevRanksRef.current && oldRank == null;
                return (
                  <WeeklyRow
                    key={entry.id}
                    entry={entry}
                    rank={i + 1}
                    isLast={i === weeklyData.entries.length - 1}
                    isFirst={i === 0}
                    rankDelta={rankDelta}
                    isNew={isNew}
                    animIndex={i}
                  />
                );
              })}
            </div>
          )}
        </div>
      )}

      {recapPhase === "intro" && recapData && (
        <WeekRecap data={recapData} onDone={startSpotlight} />
      )}
    </div>
  );
}

function WeeklyRecapRow({ p, isMe, isLast, highlight, revealed, animIndex }) {
  const [displayGolden, setDisplayGolden] = useState(0);
  const [subPhase, setSubPhase] = useState("idle"); // idle | pulse | convert | golden
  const avatarSrc = resolveAvatar(p.avatar_url);
  const isChamp = p.rank === 1;
  const targetGolden = p.golden_awarded || 0;
  const targetWindows = p.windows_held || 0;
  const localTimersRef = useRef([]);

  const clearLocal = () => {
    localTimersRef.current.forEach(t => (typeof t === "function" ? t() : clearTimeout(t)));
    localTimersRef.current = [];
  };

  // Row sub-timeline triggered by highlight.
  useEffect(() => {
    if (!highlight) {
      clearLocal();
      return;
    }
    if (targetWindows === 0 && targetGolden === 0) {
      setSubPhase("golden"); // ничего не меняется, подсвечено коротко
      return;
    }

    setSubPhase("pulse");
    setDisplayGolden(0);

    const pulseMs = 350;
    const sparkMs = 400;

    // Phase 2: spark/convert
    const t1 = setTimeout(() => setSubPhase("convert"), pulseMs);
    localTimersRef.current.push(t1);

    // Phase 3: golden shows + ticks
    const t2 = setTimeout(() => {
      setSubPhase("golden");
      if (targetGolden === 0) return;
      const g0 = performance.now();
      const goldDur = isChamp ? 1100 : 400;
      let rafG;
      const tickG = (t) => {
        const prog = Math.min(1, (t - g0) / goldDur);
        const eased = 1 - (1 - prog) * (1 - prog);
        setDisplayGolden(Math.round(eased * targetGolden));
        if (prog < 1) rafG = requestAnimationFrame(tickG);
      };
      rafG = requestAnimationFrame(tickG);
      localTimersRef.current.push(() => cancelAnimationFrame(rafG));
    }, pulseMs + sparkMs);
    localTimersRef.current.push(t2);

    return clearLocal;
  }, [highlight, isChamp, targetGolden, targetWindows]);

  // Final state after reveal.
  useEffect(() => {
    if (revealed) {
      setDisplayGolden(targetGolden);
      setSubPhase("golden");
    }
  }, [revealed, targetGolden]);

  const dim = !highlight && !revealed;
  const name = p.fio || "—";
  // Окна показываем ПО УМОЛЧАНИЮ сразу (idle). Прячем только когда переключились на golden.
  const showWindows = subPhase !== "golden";
  const showGolden = subPhase === "golden" && (highlight || revealed);

  const rowRef = useRef(null);

  // Автоскролл: когда строка становится подсвеченной, прокрутить к ней.
  useEffect(() => {
    if (highlight && rowRef.current) {
      rowRef.current.scrollIntoView({ block: "center", behavior: "smooth" });
    }
  }, [highlight]);

  return (
    <div
      ref={rowRef}
      data-rank={p.rank}
      className="relative flex items-center gap-3.5 px-4 transition-all duration-200"
      style={{
        paddingTop: 12,
        paddingBottom: 12,
        opacity: dim ? 0.4 : 1,
        background: highlight
          ? (isChamp
              ? "linear-gradient(90deg, rgba(251,191,36,0.22) 0%, rgba(251,191,36,0.06) 100%)"
              : "rgba(148,163,184,0.08)")
          : "transparent",
        animation: `weeklyRowIn 340ms cubic-bezier(0.2,0.9,0.2,1) ${animIndex * 55}ms both`,
      }}
    >
      {/* Rank */}
      <div className="relative w-6 shrink-0 flex items-center justify-center">
        <span className={`text-[13px] font-bold tabular-nums ${isChamp ? "text-amber-500" : p.rank <= 3 ? "text-slate-500 dark:text-slate-400" : "text-slate-300 dark:text-slate-600"}`}>
          {p.rank}
        </span>
      </div>

      {/* Avatar */}
      <div className="relative shrink-0">
        <div
          className="w-[42px] h-[42px] rounded-full overflow-hidden flex items-center justify-center"
          style={{
            background: "linear-gradient(135deg,#52555e 0%,#2a2c32 60%,#1a1b1f 100%)",
            border: isChamp ? "1.5px solid rgba(251,191,36,0.6)" : "none",
          }}
        >
          {avatarSrc ? (
            <img src={avatarSrc} alt="" className="w-full h-full object-cover" />
          ) : (
            <span className="text-[16px] font-semibold text-white select-none leading-none">
              {(name || "?")[0].toUpperCase()}
            </span>
          )}
        </div>
        {isChamp && (
          <img
            src="/badges/golden_crown.svg"
            alt=""
            style={{ position: "absolute", top: -22, left: 4, width: "80%", height: "80%", objectFit: "cover", pointerEvents: "none" }}
          />
        )}
      </div>

      {/* Name */}
      <div className="min-w-0 flex-1">
        <p className="text-[15px] font-medium text-slate-900 dark:text-slate-100 leading-snug flex items-center gap-1.5 min-w-0">
          <span className="truncate">{name}</span>
          {isMe && (
            <span className="text-[9px] uppercase tracking-wider font-semibold text-amber-600 dark:text-amber-300 px-1 py-[1px] rounded bg-amber-500/15 border border-amber-500/30 flex-shrink-0">
              ты
            </span>
          )}
        </p>
        <p className="text-[12px] text-slate-400 dark:text-slate-500 leading-snug mt-px truncate">
          {targetWindows > 0 ? `держал корону` : "без короны"}
        </p>
      </div>

      {/* Right side: windows (phase 1-2) → golden (phase 3) */}
      <div
        className="relative flex items-center justify-end shrink-0"
        style={{ minWidth: 86, height: 28 }}
      >
        {/* Windows big — показывается на фазе 1-2 */}
        <div
          className="absolute right-0 top-1/2 flex items-center gap-1"
          style={{
            transform: `translateY(-50%) scale(${showWindows ? 1 : 0.6})`,
            opacity: showWindows ? 1 : 0,
            transition: "transform 300ms cubic-bezier(0.34,1.56,0.64,1), opacity 250ms ease",
            pointerEvents: "none",
          }}
        >
          <img
            src="/badges/golden_crown.svg"
            alt=""
            style={{
              width: 16,
              height: 16,
              objectFit: "contain",
              filter: subPhase === "convert" ? "drop-shadow(0 0 8px rgba(251,191,36,0.8))" : "none",
              transition: "filter 300ms ease",
            }}
          />
          <span
            className="text-[16px] font-bold tabular-nums text-amber-500 dark:text-amber-400"
            style={{
              textShadow: subPhase === "convert" ? "0 0 8px rgba(251,191,36,0.6)" : "none",
              transition: "text-shadow 300ms ease",
            }}
          >
            {targetWindows}
          </span>
          <span className="text-[10px] text-slate-400 dark:text-slate-500 ml-0.5">ок</span>
        </div>

        {/* Conversion flash — короткий всплеск между фазами */}
        {highlight && subPhase === "convert" && targetGolden > 0 && (
          <span
            className="absolute right-6 top-1/2 pointer-events-none"
            style={{
              width: 24,
              height: 24,
              transform: "translateY(-50%)",
              background: "radial-gradient(circle, rgba(251,191,36,0.9) 0%, rgba(251,191,36,0) 70%)",
              borderRadius: "50%",
              animation: "convertSpark 400ms ease-out both",
            }}
          />
        )}

        {/* Golden — финальная фаза */}
        <div
          className="absolute right-0 top-1/2 flex items-center gap-1"
          style={{
            transform: `translateY(-50%) scale(${showGolden ? 1 : 0.6})`,
            opacity: showGolden ? 1 : 0,
            transition: "transform 300ms cubic-bezier(0.34,1.56,0.64,1), opacity 250ms ease",
            pointerEvents: "none",
          }}
        >
          <img
            src="/paperclip_gold.svg"
            alt=""
            style={{
              width: 18,
              height: 18,
              objectFit: "contain",
              opacity: targetGolden === 0 ? 0.3 : 1,
              filter: targetGolden === 0 ? "grayscale(1)" : "drop-shadow(0 0 4px rgba(251,191,36,0.5))",
            }}
          />
          <span
            className={`text-[14px] font-bold tabular-nums ${targetGolden === 0 ? "text-slate-400 dark:text-slate-600" : "text-amber-500 dark:text-amber-400"}`}
            style={{ minWidth: 18, textAlign: "right" }}
          >
          {displayGolden}
        </span>
        </div>
      </div>

      {!isLast && (
        <div className="pointer-events-none absolute bottom-0 left-[70px] right-0 h-px bg-slate-100 dark:bg-slate-700/50" />
      )}

      {highlight && isChamp && (
        <div className="pointer-events-none absolute inset-0 bg-amber-400" style={{ animation: "flashCham 500ms ease-out both" }} />
      )}
    </div>
  );
}

